import { FetchResult } from '@apollo/client'

import { CompanyType } from '@/models/back-office-pages/bank-statements'
import {
  ClientCommentDTO,
  DebtorAddressDTO,
  DebtorContactDTO,
  FinancingTermDTO,
  SettlementFeeType,
  SettlementStatus,
} from '@/models/back-office-pages/clients'
import { TaskAction, TaskHistoryDTO } from '@/models/back-office-pages/debt-collection'
import { InvoiceVerificationFormData } from '@/models/back-office-pages/invoice-verification'
import { FactoringProduct, FundingLine } from '@/models/back-office-pages/prospects'
import { ClientPortalInvoiceFileType } from '@/models/client-pages/invoices'
import {
  AdministrationTypes,
  Currency,
  FromToFilterInput,
  MinMaxFilterInput,
  ReportFileDTOInput,
  ReportStatus,
  SelectOption,
} from '@/models/common'
import { Direction } from '@/models/table'

// Invoices list

export const LARGE_INVOICE_AMOUNT_THRESHOLD = 50000

export enum InvoiceStatusFilter {
  ALL = 'all',
  APPROVING = 'approving',
  ARCHIVED = 'archived',
  CLOSED = 'closed',
  FUNDED = 'funded',
  NEW = 'init',
  OVERDUE = 'overdue',
  PAID = 'paid',
  READY_TO_BUY = 'ready_to_buy',
  REJECTED = 'rejected',
  SETTLING = 'settling',
  DECLINED = 'declined',
  WRITTEN_OFF = 'written_off',
}

export enum InvoiceStatus {
  NEW = 'init',
  APPROVING = 'approving',
  REJECTED = 'rejected',
  DECLINED = 'declined',
  READY_TO_BUY = 'ready_to_buy',
  FUNDED = 'funded',
  OVERDUE = 'overdue',
  SETTLING = 'settling',
  PAID = 'paid',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
  SENT_TO_INSURANCE = 'sent_to_insurance',
  SENT_TO_LEGAL = 'sent_to_legal',
  WRITTEN_OFF = 'written_off',
}

export enum BOInvoiceStatus {
  NEW = 'init',
  APPROVING = 'approving',
  REJECTED = 'rejected',
  DECLINED = 'declined',
  READY_TO_BUY = 'ready_to_buy',
  FUNDED = 'funded',
  OVERDUE = 'overdue',
  SETTLING = 'settling',
  PAID = 'paid',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
  SENT_TO_INSURANCE = 'sent_to_insurance',
  SENT_TO_LEGAL = 'sent_to_legal',
  MIGRATED_TO_SPV = 'migrated_to_spv',
  WRITTEN_OFF = 'written_off',
}

export enum InvoiceEntityType {
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
  LATENCY = 'LATENCY',
}

export enum InvoiceAutomaticValidationErrors {
  CLIENT_FINANCING_LIMIT_NOT_SUFFICIENT = 'CLIENT_FINANCING_LIMIT_NOT_SUFFICIENT',
  CLIENT_DEBTOR_CREDIT_LIMIT_NOT_SUFFICIENT = 'CLIENT_DEBTOR_CREDIT_LIMIT_NOT_SUFFICIENT',
  CLIENT_DEBTOR_LIMIT_EXPIRED = 'CLIENT_DEBTOR_LIMIT_EXPIRED',
  INVOICE_NUMBER_NOT_UNIQUE = 'INVOICE_NUMBER_NOT_UNIQUE',
  PAYMENT_TERM_EXCEEDS_CONTRACT_STIPULATION = 'PAYMENT_TERM_EXCEEDS_CONTRACT_STIPULATION',
  PAYMENT_TERM_EXCEEDS_MAX_TERM = 'PAYMENT_TERM_EXCEEDS_MAX_TERM',
  BILLING_ADDRESS_HAS_NO_VERIFIED_EMAILS = 'BILLING_ADDRESS_HAS_NO_VERIFIED_EMAILS',
  BILLING_ADDRESS_DELETED = 'BILLING_ADDRESS_DELETED',
  BILLING_ADDRESS_NOT_ASSIGNED = 'BILLING_ADDRESS_NOT_ASSIGNED',
  SPV_RISK_POLICY_BREACHED = 'SPV_RISK_POLICY_BREACHED',
  DEBTOR_BLOCKED = 'DEBTOR_BLOCKED',
  DEBTOR_INACTIVE = 'DEBTOR_INACTIVE',
  CLIENT_CONTRACT_EXPIRED = 'CLIENT_CONTRACT_EXPIRED',
  CLIENT_DEBTOR_NOT_ACTIVE_OR_APPROVED = 'CLIENT_DEBTOR_NOT_ACTIVE_OR_APPROVED',
  CLIENT_NOT_ACTIVE_OR_APPROVED = 'CLIENT_NOT_ACTIVE_OR_APPROVED',
  EXPIRY_DATE_IN_PAST = 'EXPIRY_DATE_IN_PAST',
  ISSUE_DATE_IN_FUTURE = 'ISSUE_DATE_IN_FUTURE',
  MINIMUM_FACTOR_AMOUNT_NOT_EXCEEDED = 'MINIMUM_FACTOR_AMOUNT_NOT_EXCEEDED',
  MISSING_INVOICE_ATTACHMENT = 'MISSING_INVOICE_ATTACHMENT',
  OPERATIONS_NOT_ASSIGNED = 'OPERATIONS_NOT_ASSIGNED',
  CLIENT_FINANCING_LIMIT_EXPIRED = 'CLIENT_FINANCING_LIMIT_EXPIRED',
}

export enum InvoiceExternalVerificationStatusFilter {
  APPROVED_BY_DEBTOR = 'APPROVED_BY_DEBTOR',
  APPROVED_BY_SA = 'APPROVED_BY_SA',
  N_A = 'N_A',
}

export enum InvoiceAIScrapingStatus {
  DONE = 'DONE',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
}

export interface InvoiceDTO {
  currency: Currency
  aiScrapingStatus: InvoiceAIScrapingStatus
  peppolSentStatus: PeppolSentStatus
  blockClosing: boolean
  clientCompanyName: string
  country: string
  debtorCompanyName: string
  dueDate: Date
  externalVerification?: InvoiceExternalApprovalStatus
  invoiceExternalVerificationStatus?: InvoiceExternalVerificationStatusFilter
  factorAmount: number
  fundedAmount: number
  fundedAt: Date
  fundingRequested: Date
  id: string
  invoiceAmount: number
  invoiceNumber: string
  issueDate: Date
  closedAt: Date
  overDue: number
  pendingAutomaticValidations: number
  settlements: number
  status: InvoiceStatus
  outstandingAmount: number
  automaticValidationErrors?: Array<InvoiceAutomaticValidationErrors>
  automaticExternalValidationErrors?: Array<InvoiceAutomaticValidationErrors>
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  riskMark: InvoiceRiskMarkDTO
  blockCnPayments?: boolean
  blockPnPayments?: boolean
  disabledCheckbox?: boolean
  openWriteOffAmount?: number
  writeOffAmount?: number
  paymentTerm: number
  purchasedAmount: number

  [key: string]: unknown
}

export interface InvoiceTotalsDTO {
  factorAmount: number
  invoiceAmount: number
  totalFundedAmount: number
  totalOutstandingAmount: number
  totalPurchasedAmount: number
}

export interface PageWithTotals_InvoiceDTO_InvoiceTotalsDTO {
  content: Array<InvoiceDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
  totals?: InvoiceTotalsDTO
}

export interface InvoicesData {
  invoiceList: PageWithTotals_InvoiceDTO_InvoiceTotalsDTO
}

export enum RiskMarkStatus {
  FULLY_SETTLED = 'riskMarkFullySettled',
  FRAUD = 'riskMarkFraud',
  MISPAYMENT = 'riskMarkMispayment',
  PARTIAL_PAYMENT = 'riskMarkPartialPayment',
  INSURANCE_PAYMENT = 'riskMarkInsurancePayment',
  SUSPICIOUS_TRANSACTION = 'riskMarkSuspiciousTransaction',
}

export interface InvoicesVariables {
  fundedAmount?: MinMaxFilterInput
  clientCompanyName?: string
  outstandingAmount?: MinMaxFilterInput
  dueDate?: FromToFilterInput
  factorAmount?: MinMaxFilterInput
  riskMark?: [RiskMarkStatus]
  pageSize?: number
  invoiceAmount?: MinMaxFilterInput
  tableFilteringStatus?: InvoiceStatusFilter
  pageNum?: number
  sortDirection?: Direction
  overDue?: number
  sortField?: string
  invoiceNumber?: string
  debtorCompanyName?: string
  externalVerification?: Array<InvoiceExternalApprovalStatus>
  issueDate?: FromToFilterInput
  status?: Array<InvoiceStatusFilter>
  currency?: Array<Currency>
  invoiceExternalVerificationStatus?: Array<InvoiceExternalVerificationStatusFilter>
}

export enum InvoiceReportStatus {
  PENDING = 'PENDING',
  READY = 'READY',
}

export interface InvoiceReportDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: InvoiceReportStatus
  uploader: string
}

interface Page_InvoiceReportDTO {
  content: InvoiceReportDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvoicesReportsData {
  listInvoiceReports: Page_InvoiceReportDTO
}

export interface InvoicesReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface AddInvoiceData {
  addInvoice: InvoiceDetailsDTO
}

export interface AddInvoiceVariables {
  // Invoice debtor section
  clientDebtorId: string
  billingAddressId: string
  // Invoice details section
  invoiceNumber: string
  orderNumber: string
  issueDate: Date
  dueDate: Date
  paymentTerm: number
  invoiceAmount: number
  vatAmount: number
  gAmount: number
  factorAmount: number
  // Attachment section
  invoiceFile: ReportFileDTOInput
  additionalFiles: ReportFileDTOInput[]
}

export interface AddInvoiceFormValues extends AddInvoiceVariables {
  clientId?: string
  clientName?: string
  invoiceAttachmentRequired: boolean
  canEditFactorAmount: boolean
  clientUsesGAmount?: boolean
  currency: Currency
}

export enum AddInvoiceFormType {
  INVOICES = 'INVOICES',
  CLIENT = 'CLIENT',
  DEBTOR = 'DEBTOR',
  CLIENT_PORTAL_INVOICES = 'CLIENT_PORTAL_INVOICES',
  CLIENT_PORTAL_DEBTOR = 'CLIENT_PORTAL_DEBTOR',
}

export enum AddInvoiceFieldsNames {
  CLIENT_ID = 'clientId',
  DEBTOR_ID = 'debtorId',
  CLIENT_NAME = 'clientName',
  CLIENT_DEBTOR_ID = 'clientDebtorId',
  ADDRESS = 'billingAddressId',
  INVOICE_NUMBER = 'invoiceNumber',
  ORDER_NUMBER = 'orderNumber',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  PAYMENT_TERM = 'paymentTerm',
  INVOICE_AMOUNT = 'invoiceAmount',
  VAT_AMOUNT = 'vatAmount',
  G_AMOUNT = 'gAmount',
  FACTOR_AMOUNT = 'factorAmount',
  INVOICE_FILE = 'invoiceFile',
  ADDITIONAL_FILES = 'additionalFiles',
  INVOICE_ATTACHMENT_REQUIRED = 'invoiceAttachmentRequired',
  CAN_EDIT_FACTOR_AMOUNT = 'canEditFactorAmount',
  ENABLE_PARTIAL_FACTORING = 'enablePartialFactoring',
  USES_G_AMOUNT = 'clientUsesGAmount',
  CURRENCY = 'currency',
}

export interface DownloadableFileDTO {
  content: string
  fileName: string
}

export interface DownloadInvoiceBulkImportTemplateData {
  downloadInvoiceBulkImportTemplate: DownloadableFileDTO
}

export interface DownloadInvoiceBulkImportTemplateVariables {
  language: string
}

export interface AddMultipleInvoiceData {
  addMultipleInvoice: InvoiceMultipleImportResultDTO
}

export interface AddMultipleInvoiceVariables {
  clientId: string
  file: DownloadableFileDTO
}

export interface AddMultipleInvoiceFormValue {
  clientId?: string
  clientName: string
  clientDebtorId: string
  file: DownloadableFileDTO
  importMethod?: ImportMethod
  files?: FileInputDTO[]
}

export enum AddMultipleInvoiceFieldsNames {
  FORM_TYPE = 'formType',
  CLIENT_ID = 'clientId',
  CLIENT_DEBTOR_ID = 'clientDebtorId',
  CLIENT_NAME = 'clientName',
  FILE = 'file',
  IMPORT_METHOD = 'importMethod',
  FILES = 'files',
}

export enum ImportMethod {
  EXCEL = 'EXCEL',
  AI = 'AI',
}

export interface FileInputDTO {
  filename: string
  contentBase64: string
}

export interface LineItemDTO {
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface AddInvoiceWithAiItemDTO {
  filename: string
  success: boolean
  error: string | null
  invoiceId: string | null
}

export interface AddInvoiceWithAiResultDTO {
  clientId: string
  totalFiles: number
  results: AddInvoiceWithAiItemDTO[]
}

export interface AddMultipleInvoicesWithAIData {
  processInvoices: AddInvoiceWithAiResultDTO
}

export interface AddMultipleInvoicesWithAIVariables {
  clientId: string
  files: FileInputDTO[]
}

export interface AddMultipleInvoicesWithAIFormValue {
  clientId: string
  files: FileInputDTO[]
}

export interface AddMultipleInvoiceForDebtorData {
  addMultipleInvoiceForDebtor: InvoiceMultipleImportResultDTO
}

export interface AddMultipleInvoiceForDebtorVariables {
  clientDebtorId: string
  file: DownloadableFileDTO
}

export interface CorruptedInvoiceDTO {
  billingAddressId: string
  billingAddress?: DebtorAddressDTO
  billingAddressOptions?: Array<SelectOption>
  billingAddressLoading?: boolean
  clientDebtorId: string
  debtorName?: string
  dueDate: string
  gAmount: number
  invoiceAmountInclVat: number
  invoiceNumber: string
  issueDate: string
  orderNumber: string
  paymentTerm: number
  registrationNumber?: string
  vat: number
  vatNumber?: string
  checkedForCorrection: boolean
  currency: Currency
}

export interface FormattedCorruptedInvoiceDTO {
  billingAddressId: string
  billingAddress?: DebtorAddressDTO
  billingAddressOptions?: Array<SelectOption>
  billingAddressLoading?: boolean
  clientDebtorId: string
  debtorName?: string
  dueDate: Date
  gAmount: number
  invoiceAmountInclVat: number
  invoiceNumber: string
  issueDate: Date
  orderNumber: string
  paymentTerm: number
  registrationNumber?: string
  vat: number
  vatNumber?: string
  checkedForCorrection: boolean
  currency: Currency
}

export interface InvoiceMultipleImportResultDTO {
  clientId: string
  corruptedInvoices: Array<CorruptedInvoiceDTO>
  failedImports: number
  successfullyImported: number
}

export interface ReUploadMultipleInvoiceData {
  reUploadMultipleInvoice: InvoiceMultipleImportResultDTO
}

export interface ReUploadMultipleInvoiceVariables {
  clientId: string
  successfullyImported: number
  correctedCorruptedInvoices: Array<CorruptedInvoiceDTO>
}

export interface ReUploadMultipleInvoiceFormValues {
  correctedCorruptedInvoices: Array<FormattedCorruptedInvoiceDTO>
}

export interface ReUploadMultipleInvoiceForDebtorData {
  reUploadMultipleInvoiceForDebtor: InvoiceMultipleImportResultDTO
}

export interface ReUploadMultipleInvoiceForDebtorVariables {
  clientDebtorId: string
  successfullyImported: number
  correctedCorruptedInvoices: Array<CorruptedInvoiceDTO>
}

export enum PeppolImportError {
  CLIENT_NOT_FOUND = 'CLIENT_NOT_FOUND',
  DATABASE_ERROR = 'DATABASE_ERROR',
  DEBTOR_NOT_FOUND = 'DEBTOR_NOT_FOUND',
  FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',
  INVALID_BASE64 = 'INVALID_BASE64 ',
  INVOICE_NOT_FOUND = 'INVOICE_NOT_FOUND',
  MISSING_DATA = 'MISSING_DATA ',
  UNSUPPORTED_DOCUMENT_TYPE = 'UNSUPPORTED_DOCUMENT_TYPE',
  XML_PARSER_ERROR = 'XML_PARSER_ERROR',
}

export interface PeppolErrorMessage {
  args: string[]
  code: PeppolImportError
  message: string
}

export interface PeppolBulkImportFileResultDTO {
  error: PeppolErrorMessage
  filename: string
  invoiceFileId: string
  invoiceId: string
  invoiceItemsCount: number
  paymentInfoUpdated: boolean
  pledgeTextAdded: boolean
  success: boolean
}

export interface PeppolBulkImportResultDTO {
  failedCount: number
  results: Array<PeppolBulkImportFileResultDTO>
  successCount: number
  totalFiles: number
}

export interface ImportInvoicesByPeppolUblInvoiceBulkData {
  importInvoicesByPeppolUblInvoiceBulk: PeppolBulkImportResultDTO
}

export interface ImportInvoicesByPeppolXmlUblInvoiceBulkVariables {
  clientId: string
  peppolInvoiceUblFile: Array<DownloadableFileDTO>
}

export enum ImportInvoicesByPeppolXmlFieldsNames {
  CLIENT_ID = 'clientId',
  FILES = 'peppolInvoiceUblFile',
}

export interface ArchiveInvoiceData {
  addInvoice: InvoiceDetailsDTO
}

export interface ArchiveInvoiceVariables {
  invoiceId: string
  internalComment: string
}

export interface ApproveInvoiceData {
  approveInvoice: InvoiceDetailsDTO
}

export interface ApproveInvoiceVariables {
  invoiceId: string
  internalComment: string
}

export interface RejectInvoiceData {
  rejectInvoice: InvoiceDetailsDTO
}

export interface RejectInvoiceVariables {
  invoiceId: string
  internalComment: string
  externalComment: string
}

export interface RepurchaseInvoiceByFactrisData {
  repurchaseInvoiceByFactris: boolean
}

export interface RepurchaseInvoiceByFactrisVariables {
  repurchasedAt: string
  invoiceIds: string[]
}

export enum MarkAsRepurchasedFieldNames {
  REPURCHASED_AT = 'repurchasedAt',
}

export interface MarkAsRepurchasedFormValues {
  [MarkAsRepurchasedFieldNames.REPURCHASED_AT]: string
}

export interface RevertRepurchaseInvoiceByFactrisData {
  revertRepurchaseInvoiceByFactris: boolean
}

export interface RevertRepurchaseInvoiceByFactrisVariables {
  invoiceIds: string[]
}

// Invoice details

export interface InvoiceCommentDTO {
  archived: boolean
  comment: string
  id: string
  createdBy: string
  createdByEmail: string
  createdOn: Date
  flagged: boolean
  updatedBy: string
  updatedByEmail: string
  updatedOn: Date
}

export interface InvoiceInfoCardDTO {
  clientName: string
  debtorName: string
  debtorCountry: string
  dueDate: Date
  factorAmount: number
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  gAmount: number
  invoiceAmount: number
  invoiceNumber: string
  issueDate: Date
  orderNumber: string
  paymentTerm: number
  vatAmount: number
  availableDebtorCreditLimit: number
  availableFinancingLimit: number
  purchaseRate: number
  invoiceCreatedAt: Date
  invoiceCreatedBy: string
  invoiceCreatedByClient: boolean
  requestFundingAt: Date
  requestFundingBy: string
  requestFundingByClient: boolean
  approvedAt: Date
  approvedBy: string
  purchasedAt: Date
  purchasedBy: string
  closedAt: Date
  closedBy: string
  enablePartialFactoring: boolean
  sourcingAgentAdministrates: AdministrationTypes
  debtorMismatch: boolean
  invoiceNumberMismatch: boolean
  issueDateMismatch: boolean
  dueDateMismatch: boolean
  invoiceAmountMismatch: boolean
  vatAmountMismatch: boolean
  repurchasedByFactris?: boolean
}

export enum InvoiceDetailsFieldsNames {
  ADDED_AT = 'invoiceCreatedAt',
  FACTORING_PRODUCT = 'factoringProduct',
  FUNDING_REQUESTED_AT = 'requestFundingAt',
  FUNDED_AT = 'purchasedAt',
  APPROVED_AT = 'approvedAt',
  CLIENT = 'clientName',
  DEBTOR = 'debtorName',
  CLIENT_DEBTOR_ID = 'clientDebtorId',
  CLIENT_ID = 'clientId',
  AVAILABLE_FINANCING_LIMIT = 'availableFinancingLimit',
  AVAILABLE_CREDIT_LIMIT = 'availableDebtorCreditLimit',
  INVOICE_NUMBER = 'invoiceNumber',
  ORDER_NUMBER = 'orderNumber',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  PAYMENT_TERM = 'paymentTerm',
  INVOICE_AMOUNT = 'invoiceAmount',
  VAT_AMOUNT = 'vatAmount',
  G_AMOUNT = 'gAmount',
  FACTOR_AMOUNT = 'factorAmount',
  NOT_PURCHASED_AMOUNT = 'notPurchasedAmount',
  STATUS = 'status',
  PEPPOL_STATUS = 'peppolSentStatus',
  SOURCING_AGENT = 'sourcingAgentName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  CLOSED_AT = 'closedAt',
  CAN_EDIT_FACTOR_AMOUNT = 'canEditFactorAmount',
  REPURCHASED_BY_FACTRIS = 'repurchasedByFactris',
}

export interface UpdateInvoiceDetailsData {
  editInvoice: InvoiceDetailsDTO
}

export interface UpdateInvoiceFormValues {
  currency: Currency
  invoiceCreatedAt?: Date
  invoiceCreatedBy?: string
  invoiceCreatedByClient?: boolean
  invoiceNumber: string
  orderNumber: string
  issueDate: string | Date
  dueDate: Date
  paymentTerm: number
  invoiceAmount: number
  vatAmount: number
  gAmount: number
  factorAmount: number
  purchaseRate?: number
  factoringProduct?: FactoringProduct
  clientName?: string
  debtorName?: string
  availableDebtorCreditLimit?: number
  availableFinancingLimit?: number
  clientDebtorId?: string
  clientId?: string
  status?: InvoiceStatus | BOInvoiceStatus
  peppolSentStatus?: PeppolSentStatus
  requestFundingAt?: Date
  requestFundingBy?: string
  requestFundingByClient?: boolean
  approvedAt?: Date
  approvedBy?: string
  purchasedAt?: Date
  purchasedBy?: string
  sourcingAgentAdministrates?: string
  sourcingAgentName?: string
  closedAt?: Date
  closedBy?: string
  clientUsesGAmount?: boolean
  enablePartialFactoring?: boolean
  canEditFactorAmount?: boolean
  debtorMismatch?: boolean
  invoiceNumberMismatch?: boolean
  issueDateMismatch?: boolean
  dueDateMismatch?: boolean
  invoiceAmountMismatch?: boolean
  vatAmountMismatch?: boolean
  repurchasedByFactris?: boolean
  closedWithoutCreditNote?: boolean
}

export interface UpdateInvoiceDetailsVariables extends UpdateInvoiceFormValues {
  invoiceId: string
}

export interface UpdateInvoiceClientDebtorData {
  updateInvoiceClientDebtor: InvoiceDetailsDTO
}

export interface UpdateInvoiceClientDebtorVariables {
  invoiceId: string
  clientDebtorId: string
}

export interface UpdateInvoiceNumberData {
  updateInvoiceNumber: InvoiceDetailsDTO
}

export interface UpdateInvoiceNumberVariables {
  invoiceId: string
  invoiceNumber: string
}

export interface UpdateInvoiceDueDateData {
  updateInvoiceDueDate: InvoiceDetailsDTO
}

export interface UpdateInvoiceDueDateVariables {
  invoiceId: string
  issueDate: string
  paymentTerm: number
}

export enum InvoiceLogEvent {
  AcceptIncompletePayment = 'AcceptIncompletePayment',
  Archive = 'Archive',
  DebtorApprovedVerification = 'DebtorApprovedVerification',
  DebtorRejectedVerification = 'DebtorRejectedVerification',
  SourcingAgentApprovedVerification = 'SourcingAgentApprovedVerification',
  SourcingAgentRejectedVerification = 'SourcingAgentRejectedVerification',
  RemoveResold = 'RemoveResold',
  RequestFunding = 'RequestFunding',
  RequestPartialFunding = 'RequestPartialFunding',
  RevertAcceptIncompletePayment = 'RevertAcceptIncompletePayment',
  SendVerification = 'SendVerification',
  SetResold = 'SetResold',
  SourcingAgentApproveFunding = 'SourcingAgentApproveFunding',
  SourcingAgentDeclineFunding = 'SourcingAgentDeclineFunding',
  SourcingAgentResetInvoice = 'SourcingAgentResetInvoice',
  SourcingAgentSendToInsurance = 'SourcingAgentSendToInsurance',
  full_payment_received_assigned = 'full_payment_received_assigned',
  invoice_uploaded = 'invoice_uploaded',
  invoice_purchased = 'invoice_purchased',
  invoice_risk_marks_updated = 'invoice_risk_marks_updated',
  invoice_closed = 'invoice_closed',
  invoice_details_updated = 'invoice_details_updated',
  assign_operations = 'assign_operations',
  updateLimitReserveOrderDate = 'updateLimitReserveOrderDate',
  mark_as_paid = 'mark_as_paid',
  invoice_uploaded_by_peppol_xml = 'invoice_uploaded_by_peppol_xml',
}

export interface InvoiceLogDTO {
  createdAt: Date
  event: InvoiceLogEvent
  id: string
  invoiceId: string
  userFullName: string
  externalComment: string
  internalComment: string
  debtorsEmails: string
  fullPayment: boolean
  paymentAmount: number
  bulkVerificationId: string
  peppolValidationErrors: string
  aiTaskId: string
  aiTaskNumber: string
}

export enum InvoiceExternalApprovalStatus {
  DEBTOR_HAS_NOT_REPLIED = 'DEBTOR_HAS_NOT_REPLIED',
  EXTERNAL_VERIFICATION_REQUIRED = 'EXTERNAL_VERIFICATION_REQUIRED',
  DEBTOR_REJECTED_VERIFICATION = 'DEBTOR_REJECTED_VERIFICATION',
  EXTERNAL_VERIFICATION_APPROVED_BY_DEBTOR = 'EXTERNAL_VERIFICATION_APPROVED_BY_DEBTOR',
  EXTERNAL_VERIFICATION_APPROVED_BY_SA = 'EXTERNAL_VERIFICATION_APPROVED_BY_SA',
  EXTERNAL_VERIFICATION_NOT_REQUIRED = 'EXTERNAL_VERIFICATION_NOT_REQUIRED',
  EXTERNAL_VERIFICATION_REJECTED = 'EXTERNAL_VERIFICATION_REJECTED',
  VERIFICATION_SENT_TO_DEBTOR = 'VERIFICATION_SENT_TO_DEBTOR',
}

export enum InvoiceExternalFilterApprovalStatus {
  VERIFICATION_SENT_TO_DEBTOR = 'VERIFICATION_SENT_TO_DEBTOR',
  DEBTOR_HAS_NOT_REPLIED = 'DEBTOR_HAS_NOT_REPLIED',
  EXTERNAL_VERIFICATION_NOT_REQUIRED = 'EXTERNAL_VERIFICATION_NOT_REQUIRED',
  EXTERNAL_VERIFICATION_REQUIRED = 'EXTERNAL_VERIFICATION_REQUIRED',
  EXTERNAL_VERIFICATION_REJECTED = 'EXTERNAL_VERIFICATION_REJECTED',
  DEBTOR_REJECTED_VERIFICATION = 'DEBTOR_REJECTED_VERIFICATION',
  EXTERNAL_VERIFICATION_APPROVED = 'EXTERNAL_VERIFICATION_APPROVED',
}

export interface InvoiceVerifierInput {
  email: string
  language: string
}

export enum SendVerificationEmailsFieldsNames {
  EMAILS = 'emails',
  VERIFICATION_TEMPLATE = 'verificationTemplate',
  DEBTOR_STATEMENT = 'debtorStatement',
}

export interface SendVerificationEmailsFormValues {
  emails: Array<string>
  debtorStatement: string
  verificationTemplate: InvoiceVerificationTemplate
}

export enum InvoiceVerificationTemplate {
  FIRST = 'FIRST',
  RANDOM = 'RANDOM',
}

export interface SendVerificationEmailsVariables {
  invoiceId: string
  emails: Array<InvoiceVerifierInput>
  debtorStatement: string
  verificationTemplate: InvoiceVerificationTemplate
}

export interface SendVerificationEmailsData {
  sendInvoiceVerificationEmail: InvoiceAutomaticValidationDetailsDTO
}

export interface SendBulkVerificationEmailsVariables {
  invoiceIds: string[]
  emails: Array<InvoiceVerifierInput>
  debtorStatement: string
  verificationTemplate: InvoiceVerificationTemplate
}

export interface SendBulkVerificationEmailsData {
  sendBulkInvoiceVerificationEmail: InvoiceAutomaticValidationDetailsDTO
}

export interface ApproveExternalVerificationBySAVariables extends InvoiceVerificationFormData {
  id: string
}

export interface ApproveExternalVerificationBySAData {
  approveInvoiceVerificationBySA: InvoiceAutomaticValidationDetailsDTO
}

export interface ApproveBulkExternalVerificationBySAVariables extends InvoiceVerificationFormData {
  ids: string[]
}

export interface ApproveBulkExternalVerificationBySAData {
  approveBulkInvoiceVerificationBySA: InvoiceAutomaticValidationDetailsDTO
}

export interface RejectExternalVerificationBySAVariables extends InvoiceVerificationFormData {
  id: string
}

export interface RejectExternalVerificationBySAData {
  rejectInvoiceVerificationBySA: InvoiceAutomaticValidationDetailsDTO
}

export interface RejectBulkExternalVerificationBySAVariables extends InvoiceVerificationFormData {
  ids: string[]
}

export interface RejectBulkExternalVerificationBySAData {
  rejectBulkInvoiceVerificationBySA: InvoiceAutomaticValidationDetailsDTO
}

export enum DebtorResponseFieldsNames {
  VERIFICATION_ID = 'verificationId',
  DATE = 'debtorResponseDate',
  RESPONSE = 'debtorResponse',
  FILE = 'debtorEmailFile',
  COMMENT = 'comment',
}

export interface DebtorResponseFormData extends ExternalVerificationFromEmailVariables {
  debtorResponse: boolean
}

export interface ExternalVerificationFromEmailVariables {
  debtorResponseDate: string
  debtorEmailFile: DownloadableFileDTO
  comment: string
  verificationId: string
}

export interface ApproveExternalVerificationFromEmailData {
  approveInvoiceVerificationFromDebtorEmail: InvoiceAutomaticValidationDetailsDTO
}

export interface RejectExternalVerificationFromEmailData {
  rejectInvoiceVerificationFromDebtorEmail: InvoiceAutomaticValidationDetailsDTO
}

export interface InvoiceApprovalChecklistDTO {
  automaticValidationsPassed: boolean
  externalVerificationNotRequired: boolean
  externalVerificationPassed: boolean
  externalApprovalStatus: InvoiceExternalApprovalStatus
}

export interface InvoiceClientContactsDTO {
  name: string
  phone: string
  email: string
}

export enum InvoiceVerificationResponse {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface InvoiceVerificationDecisionDTO {
  comment: string
  debtorsEmailDocumentId: string
  debtorsEmailDocumentName: string
  decision: InvoiceVerificationResponse
  decisionAt: Date
  decisionBy: string
  sourcingAgentUserName: string
  sourcingAgentEnteredDecisionAt: Date
}

export interface InvoiceVerifierDTO {
  id: string
  email: string
  sentAt: Date
  debtorStatementAttached: boolean
  verificationTemplate: InvoiceVerificationTemplate
}

export interface InvoiceExternalVerificationDetailsDTO {
  verifiers: Array<InvoiceVerifierDTO>
  clientContacts: InvoiceClientContactsDTO
  debtorDecision: InvoiceVerificationDecisionDTO
  operationDecision: InvoiceVerificationDecisionDTO
  otherDecisions: Array<InvoiceVerificationDecisionDTO>
}

export interface InvoiceRiskMarkDTO {
  riskMarkFraud: boolean
  riskMarkMispayment: boolean
  riskMarkPartialPayment: boolean
  riskMarkInsurancePayment: boolean
  riskMarkFullySettled: boolean
  riskMarkSuspiciousTransaction: boolean
}

export enum InvoiceReconciliationType {
  FORGONE_AMOUNT_NOT_FACTORED = 'FORGONE_AMOUNT_NOT_FACTORED',
  PAYMENT_RECEIVED = 'PAYMENT_RECEIVED',
  SETTLEMENT = 'SETTLEMENT',
  WRITE_OFF = 'WRITE_OFF',
}

export interface InvoiceReconciliationDTO {
  amount: number
  date: Date
  reconciliationType: InvoiceReconciliationType
  dilutionType: SettlementDilutionType
  companyIban: string
  companyName: string
  companyType: CompanyType
  reconciliationId: string
  transactionId: string
  bankReference: string
  riskMarkTransactionMadeByThirdParty: boolean
  riskMarkTransactionSuspendedByBank: boolean
}

export interface InvoicePurchaseDetailsDTO {
  accruedIncreasedFees: number
  accruedInterests: number
  interestFeesVat: number
  advanceFactorFee: number
  factorAmount: number
  fundedDays: number
  invoiceReturnAmount: number
  maxPartlyRecourseSettlementAmount: number
  maxFullyRecourseSettlementAmount: number
  openAmount: number
  overdueDays: number
  payout: number
  upFrontFees: number
  vatTotal: number
  purchaseNoteNumber: string
  reconciliations: Array<InvoiceReconciliationDTO>
}

export interface InvoiceSettlementDTO {
  amount: number
  createdBy: string
  createdOn: Date
  description: string
  feeType: SettlementFeeType
  id: string
  openAmount: number
  status: SettlementStatus
  usedAmount: number
  vat: number
}

export interface DebtorIdentificationNumbersDTO {
  registrationNumber: string
  vat: string
}

export interface InvoiceCreditPayoutDetailsDTO {
  advanceFactorFee: number
  closedAt: Date
  closedBy: string
  creditNoteNumber: string
  fees: number
  notPurchasedAmount: number
  payout: number
  vatTotal: number
}

export enum InvoiceAutomaticValidationWarnings {
  HAS_DUPLICATE_INVOICE_AMOUNT = 'HAS_DUPLICATE_INVOICE_AMOUNT',
  HAS_SIMILAR_INVOICE_NUMBERS = 'HAS_SIMILAR_INVOICE_NUMBERS',
  HAS_OVERDUE = 'HAS_OVERDUE',
  OPERATIONS_NOT_ASSIGNED = 'OPERATIONS_NOT_ASSIGNED',
  CLIENT_DEBTORS_FIRST_INVOICE = 'CLIENT_DEBTORS_FIRST_INVOICE',
  DEBTOR_FROM_GERMANY = 'DEBTOR_FROM_GERMANY',
  MISMATCH_WITH_PEPPOL_XML = 'MISMATCH_WITH_PEPPOL_XML',
  DEBTOR_HAS_OVERDUES = 'DEBTOR_HAS_OVERDUES',
  NOT_CC_TO_DEBTORS_EMAIL = 'NOT_CC_TO_DEBTORS_EMAIL',
  HAS_NO_PAYMENT_HISTORY_WITH_FACTRIS = 'HAS_NO_PAYMENT_HISTORY_WITH_FACTRIS',
}

export interface InvoiceAmountDTO {
  factorAmount: number
  gAmount: number
  invoiceAmount: number
  vatAmount: number
}

export interface InvoicePartialFundingAmountsDTO {
  clientsAvailableFinancingLimit: number
  debtorsAvailableCreditLimit: number
  maxAvailableFactorAmount: number
}

export interface InvoicePartialFundingDetailsDTO {
  partialFactorAmount: number
  requestAt: Date
  requestBy: string
  requestedByClient: boolean
}

export enum BillingSettlementStatus {
  NEW = 'NEW',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

export enum BillingSettlementType {
  INVOICE_EXTRA_FEES = 'INVOICE_EXTRA_FEES',
  INVOICE_WRITE_OFF = 'INVOICE_WRITE_OFF',
}

export interface InvoiceBillingSettlementDTO {
  amount: number
  createdBy: string
  createdOn: Date
  id: string
  openAmount: number
  status: BillingSettlementStatus
  billingSettlementType: BillingSettlementType
  usedAmount: number
  description: string
}

export interface InvoiceWriteOffAmountsDTO {
  openFundedAmount: number
  realizedInterests: number
  total: number
}

export enum PeppolSentStatus {
  NOT_SENT = 'NOT_SENT',
  SENDING = 'SENDING',
  SENT_SUCCESS = 'SENT_SUCCESS',
  SENT_FAILURE = 'SENT_FAILURE',
}

export interface PeppolPartyData {
  additionalStreetName: string
  cityName: string
  companyId: string
  contactEmail: string
  contactName: string
  contactTelephone: string
  countryCode: string
  endpointId: string
  name: string
  postalZone: string
  registrationName: string
  streetName: string
  vatNumber: string
}

export enum VatCategory {
  CANARY_ISLANDS = 'CANARY_ISLANDS',
  CEUTA_MELILLA = 'CELLA_MELILLA',
  EXEMPT = 'EXEMPT',
  EXPORT = 'EXPORT',
  INTRA_COMMUNITY = 'INTRA_COMMUNITY',
  NOT_SUBJECT = 'NOT_SUBJECT',
  OTHER = 'OTHER',
  REVERSE_CHARGE = 'REVERSE_CHARGE',
  SERVICE_OUTSIDE_SCOPE = 'SERVICE_OUTSIDE_SCOPE',
  STANDARD = 'STANDARD',
  ZERO_RATED = 'ZERO_RATED',
}

export interface InvoiceItemDTO {
  description: string
  qty: number
  total: number
  unitPrice: number
  vat: number
  vatCategory: VatCategory
}

export interface GeneralInformation {
  buyer: PeppolPartyData
  seller: PeppolPartyData
  invoiceNumber: string
  issueDate: Date
  dueDate: Date
  payeePartyName: string
  payeeIban: string
  payeeNote: string
}

export interface PeppolTotals {
  totalCharges: number
  totalAllowances: number
  totalExcludingVat: number
  totalVatAmount: number
  totalIncludingVat: number
  totalNetAmount: number
  prepaidAmount: number
  payableAmount: number
  payableRoundingAmount: number
}

export interface PeppolUploadedFileInformation {
  uploadedPeppolUblBy: string
  uploadedPeppolUblByAt: Date
  uploadedPeppolUblFileId: string
  uploadedPeppolUblFilename: string
}

export interface PeppolSentInformation {
  peppolSentAt: Date
  peppolSentBy: string
  peppolSentStatus: PeppolSentStatus
}

export interface InvvoicePeppolXmlDetailsDTO {
  buyer: PeppolPartyData
  dueDate: Date
  invoiceItems: Array<InvoiceItemDTO>
  invoiceNumber: string
  issueDate: Date
  mismatchWithInvoiceDataFound: Boolean
  note: string
  payableAmount: number
  payableRoundingAmount: number
  payeeIban: string
  payeePartyName: string
  paymentTermsNote: string
  peppolSentAt: Date
  peppolSentBy: string
  prepaidAmount: number
  seller: PeppolPartyData
  totalAllowances: number
  totalCharges: number
  totalExcludingVat: number
  totalIncludingVat: number
  totalNetAmount: number
  totalVatAmount: number
  uploadedPeppolUblBy: string
  uploadedPeppolUblByAt: Date
  uploadedPeppolUblByEmail: string
  uploadedPeppolUblFileId: string
  uploadedPeppolUblFilename: string
}

export interface InvoiceDetailsDTO {
  currency: Currency
  blockClosing: boolean
  clientComments: InvoiceCommentDTO[]
  clientDebtorId: string
  clientId: string
  debtorId: string
  debtorComments: InvoiceCommentDTO[]
  debtCollectionsComments: InvoiceCommentDTO[]
  details: InvoiceInfoCardDTO
  id: string
  status: BOInvoiceStatus
  logs: InvoiceLogDTO[]
  billingAddress: DebtorContactDTO
  assigneeEmail: string
  sourcingAgentCountry: string
  sourcingAgentId: string
  riskMarks: InvoiceRiskMarkDTO
  purchaseDetails: InvoicePurchaseDetailsDTO
  creditPayoutDetails: InvoiceCreditPayoutDetailsDTO
  settlements: Array<InvoiceSettlementDTO>
  billingSettlements: Array<InvoiceBillingSettlementDTO>
  debtorIdentificationNumbers: DebtorIdentificationNumbersDTO
  blockCnPayments: boolean
  blockPnPayments: boolean
  hasPartialFundingAvailability: boolean
  amounts: InvoiceAmountDTO
  partialFundingAmounts: InvoicePartialFundingAmountsDTO
  partialFundingDetails: InvoicePartialFundingDetailsDTO
  limitReserveOrderDate: Date
  allowMarkInvoiceAsPaid: boolean
  allowToWriteOff: boolean
  writeOffAmounts: InvoiceWriteOffAmountsDTO
  allowToRevertWriteOff: boolean
  accruedInterests: number
  enablePartialFactoring: boolean
  peppolSendEnabled: boolean
  peppolDisabledForDebtor: boolean
  peppolSentStatus: PeppolSentStatus
  peppolXmlDetails: InvvoicePeppolXmlDetailsDTO
  repurchasedByFactris: boolean
  closedWithoutCreditNote: boolean
}

export interface InvoiceDetailsData {
  loadInvoiceDetails: InvoiceDetailsDTO
}

export interface InvoiceDetailVariables {
  id: string
}

export enum InvoiceExternalValidationReason {
  EACH_INVOICE_CHECK = 'EACH_INVOICE_CHECK',
  FIRST_DEBTOR_INVOICE_CHECK = 'FIRST_DEBTOR_INVOICE_CHECK',
  PERIODICAL_CHECK = 'PERIODICAL_CHECK',
  RANDOM_INVOICE_CHECK = 'RANDOM_INVOICE_CHECK',
  THRESHOLD_EXCEEDED = 'THRESHOLD_EXCEEDED',
  VARIANCE_EXCEEDS = 'VARIANCE_EXCEEDS',
}

export interface InvoiceAutomaticValidationDetailsDTO {
  automaticValidationErrors: InvoiceAutomaticValidationErrors[]
  externalValidationErrors: InvoiceAutomaticValidationErrors[]
  automaticValidationWarnings: InvoiceAutomaticValidationWarnings[]
  externalValidationReasons: InvoiceExternalValidationReason[]
  externalVerificationDecision: InvoiceExternalVerificationDetailsDTO
  approvalChecklist: InvoiceApprovalChecklistDTO
}

export interface InvoiceValidationDetailsData {
  loadInvoiceAutomaticValidationDetails: InvoiceAutomaticValidationDetailsDTO
}

export interface InvoiceValidationDetailsVariables {
  id: string
}

export interface InvoiceBatchVerificationDTO {
  approvedCount: number
  inApprovingCount: number
  invoices: InvoiceDTO[]
  rejectedCount: number
}

export interface ApproveInvoicesData {
  approveInvoices: InvoiceBatchVerificationDTO
}

export interface ApproveInvoicesVariables {
  invoiceIds: Array<string>
  internalComment: string
}

export interface RejectInvoicesData {
  rejectInvoices: InvoiceBatchVerificationDTO
}

export interface RejectInvoicesVariables {
  invoiceIds: Array<string>
  internalComment: string
  externalComment: string
}

export interface ArchiveInvoicesData {
  archiveInvoices: InvoiceBatchVerificationDTO
}

export interface ArchiveInvoicesVariables {
  invoiceIds: Array<string>
  internalComment: string
}

export interface FundInvoicesData {
  fundInvoices: InvoiceBatchVerificationDTO
}

export interface FundInvoicesVariables {
  invoiceIds: Array<string>
}

export interface FundInvoiceData {
  fundInvoice: InvoiceDetailsDTO
}

export interface FundInvoiceVariables {
  invoiceId: string
}

export interface PartlyFundInvoiceData {
  partlyFundInvoice: InvoiceDetailsDTO
}

export interface PartlyFundInvoiceVariables {
  invoiceId: string
}

export interface MarkAsPaidInvoiceData {
  markInvoiceAsPaid: InvoiceDetailsDTO
}

export interface MarkAsPaidInvoiceVariables {
  invoiceId: string
}

export interface BlockCloseInvoiceData {
  blockClosingInvoice: InvoiceDetailsDTO
}

export interface BlockCloseInvoiceVariables {
  invoiceId: string
}

export interface UnblockCloseInvoiceData {
  revertBlockClosingInvoice: InvoiceDetailsDTO
}

export interface UnblockCloseInvoiceVariables {
  invoiceId: string
}

export interface CloseInvoiceWithoutCNData {
  closeInvoiceWithoutCreditNote: Pick<InvoiceDetailsDTO, 'id' | 'status'>
}

export interface CloseInvoiceWithoutCNVariables {
  invoiceId: string
}

export interface WriteOffInvoiceData {
  writeOffInvoice: InvoiceDetailsDTO
}

export interface WriteOffInvoiceVariables {
  invoiceId: string
}

export interface RevertWriteOffInvoiceData {
  revertWriteOffInvoice: InvoiceDetailsDTO
}

export interface RevertWriteOffInvoiceVariables {
  invoiceId: string
}

export interface PurchaseOrCloseClientDTO {
  clientId: string
  clientName: string
  currency: Currency
  fundingLineType: FundingLine
  iban: string
}

export interface PurchaseOrCloseDTO {
  administration: string
  contractSourcingAgentId: string
  client: PurchaseOrCloseClientDTO
  invoiceContractRates: Array<InvoiceContractRatesDTO>
  settlements: Array<InvoiceSettlementForDiscountDTO>
}

export interface InvoicesForPurchaseDTO {
  administrations: Array<AdministrationTypes>
  purchases: Array<PurchaseOrCloseDTO>
}

export interface InvoicesForBuyData {
  invoicesForBuy: InvoicesForPurchaseDTO
}

export interface InvoicesForBuyVariables {
  invoices: Array<string>
}

export interface InvoicesForCloseData {
  invoicesForClose: InvoicesForPurchaseDTO
}

export interface InvoicesForCloseVariables {
  invoices: Array<string>
}

export interface PeppolSendEligibilityDTO {
  eligible: Array<InvoiceDTO>
  eligibleCount: number
}

export interface PreparePeppolSendData {
  preparePeppolSendList: PeppolSendEligibilityDTO
}

export interface PreparePeppolSendVariables {
  invoiceIds: Array<string>
}

export interface BulkVerificationClientDebtorDTO {
  clientDebtorId: string
  clientId: string
  clientName: string
  debtorName: string
}

export interface BulkVerificationDTO {
  clientDebtor: BulkVerificationClientDebtorDTO
  invoices: Array<InvoiceDTO>
}

export interface InvoicesForBulkVerificationsDTO {
  verifications: Array<BulkVerificationDTO>
}

export interface InvoicesForBulkVerificationsData {
  invoicesForBulkVerifications: InvoicesForBulkVerificationsDTO
}

export interface InvoicesForBulkVerificationsVariables {
  invoices: Array<string>
  includeDebtorRejected: boolean
}

export interface VerifyInvoicesFormProps {
  invoices: Array<InvoiceDTO>
  clientDebtor: BulkVerificationClientDebtorDTO
  status: InvoiceStatusFilter
  onClose: () => void
  updateReadyToVerifyInvoices: (clientDebtorId: string) => Promise<void>
  refetchInvoices: () => Promise<void>
}

export interface InvoiceMultiplePurchaseResultDTO {
  purchaseNoteId: string
  purchaseNoteNumber: string
}

export interface BuyMultipleInvoicesData {
  buyMultipleInvoice: InvoiceMultiplePurchaseResultDTO
}

export interface BuyMultipleInvoicesVariables {
  invoiceIds: Array<string>
  settlementDiscounts: Array<SettlementDiscountDTOInput>
}

export interface InvoiceMultipleCloseResultDTO {
  creditNoteId: string
  creditNoteNumber: string
}

export interface CloseMultipleInvoicesData {
  closeMultipleInvoice: InvoiceMultipleCloseResultDTO
}

export interface CloseMultipleInvoicesVariables {
  invoiceIds: Array<string>
  settlementDiscounts: Array<SettlementDiscountDTOInput>
}

export interface PurchaseMultipleInvoicesProps {
  readyInvoices: InvoicesForPurchaseDTO
  setReadyInvoices: (readyInvoices: InvoicesForPurchaseDTO) => void
  setSelectedAdministration: (administration: AdministrationTypes) => void
  setSelectedTabKey: (clientId: string) => void
  clientId: string
  fundingLine: FundingLine
  iban: string
  refetchInvoices: () => Promise<void>
  selectedAdministration: AdministrationTypes
}

export interface PurchaseInvoicesFormProps {
  currency: Currency
  invoiceContractRates: Array<InvoiceContractRatesDTO>
  purchaseType: PurchaseType
  onClose: () => void
  settlements: Array<InvoiceSettlementForDiscountDTO>
  loadingSettlement?: boolean
  errorMessage: string
  purchaseMultipleInvoices: (
    ids: Array<string>,
    settlementDiscounts: Array<SettlementDiscountDTOInput>,
  ) => Promise<FetchResult<CloseMultipleInvoicesData | CloseInvoiceData | BuyMultipleInvoicesData | BuyInvoiceData>>
  purchasingInvoices: boolean
  multipleInvoicesProps?: PurchaseMultipleInvoicesProps
}

export interface ResetToNewInvoicesData {
  resetToNewInvoices: InvoiceBatchVerificationDTO
}

export interface ResetToNewInvoicesVariables {
  invoiceIds: Array<string>
}

export interface ResetToNewInvoiceData {
  resetToNewInvoice: InvoiceDetailsDTO
}

export interface ResetToNewInvoiceVariables {
  invoiceId: string
}

export interface SettlementDiscountDTOInput {
  discountAmount: number
  settlementId: string
}

export interface BuyInvoiceData {
  buyInvoice: InvoiceDetailsDTO
}

export interface BuyInvoiceVariables {
  invoiceId: string
  settlementDiscounts: Array<SettlementDiscountDTOInput>
}

export interface CloseInvoiceData {
  closeInvoice: InvoiceDetailsDTO
}

export interface CloseInvoiceVariables {
  invoiceId: string
  settlementDiscounts: Array<SettlementDiscountDTOInput>
}

export enum PurchaseType {
  BUY = 'BUY',
  CLOSE = 'CLOSE',
}

export interface InvoiceContractRatesDTO {
  administrationFee: number
  advanceFactorFee: number
  advancePayable: number
  amountNotFactored: number
  currency: Currency
  debtorName: string
  factorAmount: number
  factorFee: number
  invoiceAmount: number
  invoiceId: string
  invoiceNumber: string
  issueDate: Date
  nonRecourseFee: number
  refund: number
  refundInclVat: number
  refundVat: number
  reverseChargeVat: boolean
  fixedFeesForCreditNote: number
  fixedFeesForPurchaseNote: number
}

export interface InvoiceContractRatesData {
  invoiceContractRates: Array<InvoiceContractRatesDTO>
}

export interface InvoiceContractRatesVariables {
  ids: Array<string>
}

export interface InvoiceSettlementForDiscountDTO {
  amount: number
  amountInclVat: number
  createdOn: Date
  description: string
  feeType: SettlementFeeType
  id: string
  vat: number
  currency: Currency
  sameSourcingAgent: boolean
  sourcingAgentAdministrates: AdministrationTypes
}

export interface InvoiceSettlementsForDiscountsData {
  invoiceSettlementsForDiscounts: Array<InvoiceSettlementForDiscountDTO>
}

export interface InvoiceSettlementsForDiscountsVariables {
  clientId: string
  currency: Currency
  sourcingAgentId: string
}

export interface InvoiceSettlementForDiscountFormData extends InvoiceSettlementForDiscountDTO {
  discountAmount: number
  discountAmountWithVat: number
  remainingAmount: number
}

export interface InvoicePurchaseNoteFileDTO {
  content: string
  fileName: string
  size: number
}

export interface DownloadInvoicePurchaseNoteData {
  downloadInvoicePurchaseNote: InvoicePurchaseNoteFileDTO
}

export interface DownloadInvoicePurchaseNoteVariables {
  id: string
}

export interface DownloadInvoiceCreditNoteData {
  downloadInvoiceCreditNote: InvoicePurchaseNoteFileDTO
}

export interface DownloadInvoiceCreditNoteVariables {
  id: string
}

// Reports tab

export enum InvoiceFileType {
  INVOICE = 'INVOICE',
  INTERNAL = 'INTERNAL',
  OTHER = 'OTHER',
}

export interface InvoiceReportFileDTO {
  clientDebtorId: string
  content: string
  description: string
  documentId: string
  fileName: string
  reportCreatedAt: string
  reportType: InvoiceFileType | ClientPortalInvoiceFileType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_InvoiceReportFileDTO {
  content: InvoiceReportFileDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvoiceReportsData {
  invoiceReports: Page_InvoiceReportFileDTO
}

export interface InvoiceReportsVariables {
  invoiceId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadInvoiceReportData {
  downloadInvoiceReport: InvoiceReportFileDTO
}

export interface DownloadInvoiceReportVariables {
  documentId: string
}

export interface UploadInvoiceReportData {
  uploadInvoiceReport: string
}

export interface UploadInvoiceReportVariables {
  reportType: InvoiceFileType | string
  invoiceId?: string
  file: ReportFileDTOInput | string
  reportCreatedAt: string
  description?: string
}

export interface UploadInvoiceReportFormValues {
  reportType: InvoiceFileType | string
  invoiceId?: string
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description?: string
}

export interface DeleteInvoiceReportData {
  deleteInvoiceReport: InvoiceReportFileDTO
}

export interface DeleteInvoiceReportVariables {
  documentId: string
}

export interface RefreshInvoiceLimitChecksData {
  refreshInvoiceLimitChecks: boolean
}

export interface ChangeInvoiceBillingAddressData {
  changeInvoiceBillingAddress: InvoiceDetailsDTO
}

export interface ChangeInvoiceBillingAddressFormValues {
  billingAddressId: string
}

export interface ChangeInvoiceBillingAddressVariables extends ChangeInvoiceBillingAddressFormValues {
  invoiceId: string
}

export enum ChangeInvoiceBillingAddressFieldsNames {
  ADDRESS = 'billingAddressId',
}

export interface AssignOperationToInvoiceData {
  assignOperationsToInvoice: InvoiceDetailsDTO
}

export interface AssignOperationToInvoiceFormValues {
  assigneeEmail: string
}

export enum AssignOperationToInvoiceFieldsNames {
  EMAIL = 'assigneeEmail',
}

export interface AssignOperationToInvoiceVariables {
  invoiceId: string
  assigneeEmail: string
}

export interface OperationUserDTO {
  email: string
}

export interface OperationsData {
  getOperations: OperationUserDTO[]
}

export interface OperationsVariables {
  country: string
}

export enum InvoiceFormFieldsNames {
  ID = 'id',
  INTERNAL_COMMENT = 'internalComment',
  EXTERNAL_COMMENT = 'externalComment',
  WITH_EXTERNAL_COMMENT = 'withExternalComment',
}

export enum BuyInvoiceFormFieldArrayNames {
  AMOUNT = 'amount',
  DISCOUNT_AMOUNT = 'discountAmount',
  DISCOUNT_AMOUNT_WITH_VAT = 'discountAmountWithVat',
  REMAINING_AMOUNT = 'remainingAmount',
}

export enum BuyInvoiceFormFieldsNames {
  IDS = 'ids',
  SETTLEMENT_DISCOUNTS = 'settlementDiscounts',
  SELECTED_SA_SETTLEMENT_DISCOUNTS = 'selectedAdministrationSettlementDiscounts',
  OTHER_SA_SETTLEMENT_DISCOUNTS = 'otherAdministrationSettlementDiscounts',
  SHOW_TABLE_WITH_OTHER_ADMINISTRATION = 'showTableWithOtherAdministrations',
}

export interface UpdateInvoiceRiskMarksData {
  updateInvoiceRiskMark: InvoiceRiskMarkDTO
}

export interface UpdateInvoiceRiskMarksFormValues {
  riskMarkFraud: boolean
  riskMarkMispayment: boolean
  riskMarkPartialPayment: boolean
  riskMarkFullySettled: boolean
  riskMarkSuspiciousTransaction: boolean
}

export enum UpdateInvoiceRiskMarksFieldsNames {
  FRAUD = 'riskMarkFraud',
  MISPAYMENT = 'riskMarkMispayment',
  PARTIAL_PAYMENT = 'riskMarkPartialPayment',
  INSURANCE_PAYMENT = 'riskMarkInsurancePayment',
  FULLY_SETTLED = 'riskMarkFullySettled',
  SUSPICIOUS_TRANSACTION = 'riskMarkSuspiciousTransaction',
}

export interface UpdateInvoiceRiskMarkseVariables {
  riskMarkFraud: boolean
  riskMarkSuspiciousTransaction: boolean
  invoiceId: string
}

export enum SettleInvoiceFormFieldsNames {
  ID = 'invoiceId',
  RECOURSE_TYPE = 'recourseType',
  PARTLY_RECOURSE_AMOUNT = 'recourseAmount',
  MAX_PARTLY_RECOURSE_AMOUNT = 'maxPartiallyRecourseAmount',
  DILUTION_TYPE = 'dilutionType',
  CLIENT_CREDIT_NOTE_ID = 'clientCreditNoteId',
}

export enum SettlementDilutionType {
  credit_adjustment = 'credit_adjustment',
  discount = 'discount',
  dispute = 'dispute',
  offset = 'offset',
  paid_by_client = 'paid_by_client',
  transfer_charges = 'transfer_charges',
  other = 'other',
}

export enum RecourseType {
  FULLY = 'FULLY',
  PARTLY = 'PARTLY',
}

export interface SettleInvoiceFormProps {
  recourseType: RecourseType
  dilutionType: SettlementDilutionType
  recourseAmount: number
  maxPartiallyRecourseAmount: number
  clientCreditNoteId?: string
}

export interface FullyRecourseInvoiceData {
  fullyRecourseInvoice: InvoiceDetailsDTO
}

export interface FullyRecourseInvoiceVariables {
  invoiceId: string
  dilutionType: SettlementDilutionType
  clientCreditNoteId?: string
}

export interface PartlyRecourseInvoiceData {
  partlyRecourseInvoice: InvoiceDetailsDTO
}

export interface PartlyRecourseInvoiceVariables {
  invoiceId: string
  dilutionType: SettlementDilutionType
  recourseAmount: number
  clientCreditNoteId?: string
}

export interface AddInvoiceCommentData {
  addInvoiceComment: InvoiceCommentDTO
}

export interface AddInvoiceCommentVariables {
  invoiceId: string
  comment: string
}

export interface UpdateInvoiceCommentData {
  updateInvoiceComment: ClientCommentDTO
}

export interface UpdateInvoiceCommentVariables {
  comment: string
  commentId: string
}

export interface MarkInvoiceCommentAsFlaggedData {
  markInvoiceCommentAsFlagged: ClientCommentDTO
}

export interface MarkInvoiceCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkInvoiceCommentAsUnflaggedData {
  markInvoiceCommentAsUnflagged: ClientCommentDTO
}

export interface MarkInvoiceCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkInvoiceCommentAsArchivedData {
  markInvoiceCommentAsArchived: ClientCommentDTO
}

export interface MarkInvoiceCommentAsArchivedVariables {
  commentId: string
}

export interface MarkInvoiceCommentAsUnarchivedData {
  markInvoiceCommentAsUnarchived: ClientCommentDTO
}

export interface MarkInvoiceCommentAsUnarchivedVariables {
  commentId: string
}

export interface GetInvoiceCommentsData {
  getInvoiceComments: Array<InvoiceCommentDTO>
}

export interface GetInvoiceCommentsVariables {
  invoiceId: string
}

export interface GetInvoiceCommentByIdData {
  getInvoiceCommentById: InvoiceCommentDTO
}

export interface GetInvoiceCommentsByIdVariables {
  commentId: string
}

export interface InvoiceContractDTO {
  financingTerm: FinancingTermDTO
}

export interface InvoiceContractDetailsData {
  invoiceContractDetails: InvoiceContractDTO
}

export interface InvoiceContractDetailsVariables {
  id: string
}

export interface InvoiceReportResponseDTO {
  reportId: string
}

export interface RequestGenerateInvoicesReportData {
  requestInvoiceReport: InvoiceReportResponseDTO
}

export interface RequestGenerateInvoicesReportVariables {
  fundedAmount?: MinMaxFilterInput
  fundedAt?: FromToFilterInput
  feesAdjustmentsPeriod?: FromToFilterInput
  overDue?: number
  clientCompanyName?: string
  outstandingAmount?: MinMaxFilterInput
  dueDate?: FromToFilterInput
  factorAmount?: MinMaxFilterInput
  invoiceNumber?: string
  debtorCompanyName?: string
  invoiceAmount?: MinMaxFilterInput
  tableFilteringStatus: InvoiceStatusFilter
  issueDate?: FromToFilterInput
  status?: Array<InvoiceStatusFilter>
  sourcingAgentAdministrates?: Array<AdministrationTypes>
  currency?: Array<Currency>
}

export enum GenerateReportFormFieldsNames {
  FEES_ADJUSTMENT_PERIOD = 'feesAdjustmentsPeriod',
}

export interface RequestGenerateInvoicesReportFormData {
  feesAdjustmentsPeriod?: FromToFilterInput
}

export interface DownloadInvoicesReportData {
  downloadGeneratedInvoiceReport: InvoiceReportFileDTO
}

export interface DownloadInvoicesReportVariables {
  documentId: string
}

export interface CancelInvoicesReportData {
  cancelInvoiceReport: InvoiceReportResponseDTO
}

export interface CancelInvoicesReportVariables {
  reportId: string
}

// Debt collection

interface InvoiceTasksDetailsDTO {
  tasks: TaskHistoryDTO[]
}

export interface InvoiceTasksData {
  invoiceTasks: InvoiceTasksDetailsDTO
}

export interface InvoiceTasksVariables {
  invoiceId: string
}

export interface UpdateLimitReserveOrderDateData {
  updateLimitReserveOrderDate: InvoiceDetailsDTO
}

export interface UpdateLimitReserveOrderDateVariables {
  limitReserveOrderDate: string
  invoiceId: string
}

export interface UpdateLimitReserveOrderFormValues {
  limitReserveOrderDate: Date
}

export enum LimitReservePriorityFieldsNames {
  PRIORITY_DATE = 'limitReserveOrderDate',
}

export interface AddCommentToInvoicesData {
  archiveInvoices: InvoiceBatchVerificationDTO
}

export interface AddCommentToInvoicesVariables {
  invoiceIds: Array<string>
  comment: string
  markAsFlagged: boolean
  fromCmpTask?: boolean
}

export enum AddCommentToInvoiceFormFieldsNames {
  INVOICE_IDS = 'invoiceIds',
  COMMENT = 'comment',
  MARK_AS_FLAGGED = 'markAsFlagged',
}

export interface DeleteExtraFeesForInvoiceData {
  deleteExtraFeesForInvoice: InvoiceDetailsDTO
}

export interface DeleteExtraFeesForInvoiceVariables {
  id: string
}

export interface AddExtraFeesForInvoiceData {
  addExtraFeesForInvoice: InvoiceDetailsDTO
}

export interface AddExtraFeesForInvoiceVariables {
  feesAmount: number
  description: string
  invoiceId: string
}

export enum AddExtraFeesForInvoiceFieldsNames {
  AMOUNT = 'feesAmount',
  DESCRIPTION = 'description',
  ID = 'invoiceId',
}

export interface InvoiceStatusDTO {
  id: string
  status: BOInvoiceStatus
}

export interface AddNewTaskForInvoiceData {
  createAdditionalTaskForInvoice: InvoiceStatusDTO
}

export interface AddNewTaskForInvoiceVariables {
  taskInformation: string
  dueDate: string
  taskAction: TaskAction
  invoiceId: string
}

export interface ClientDebtorRiskReportResponseDTO {
  reportId: string
}

export interface RequestClientDebtorRiskReportData {
  clientDebtorRiskReportRequest: ClientDebtorRiskReportResponseDTO
}

export interface RequestClientDebtorRiskReportVariables {
  dateFilter: FromToFilterInput
}

export interface RequestClientDebtorRiskReportFormData {
  dateFilter: FromToFilterInput
}

export enum GenerateClientDebtorRiskReportFieldsNames {
  DATE_FILTER = 'dateFilter',
}

export interface DownloadClientDebtorRiskReportData {
  downloadClientDebtorRiskReport: ReportFileDTO
}

export interface DownloadClientDebtorRiskReportVariables {
  documentId: string
}

export interface CancelClientDebtorRiskReportData {
  cancelClientDebtorRiskReport: ClientDebtorRiskReportResponseDTO
}

export interface CancelClientDebtorRiskReportVariables {
  reportId: string
}

export interface ReportFileDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: ReportStatus
  uploader: string
  content: string
}

interface Page_ReportFileDTO {
  content: ReportFileDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientDebtorRiskReportsData {
  clientDebtorRiskReportList: Page_ReportFileDTO
}

export interface ClientDebtorRiskReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export const commentApprovalTemplates = [
  'PO/PN/CN attached',
  'Signed timesheets',
  'Selfbilling attached',
  'Prefacturation list attached',
  'Debtor statement',
  'Approved hours',
  'No overdues',
  'Regular amount',
  'Payment experience',
  'Debtor confirmed by e-mail',
  'Factris in cc.',
]

export interface UploadPeppolXmlFileData {
  attachPeppolUblInvoiceFile: boolean
}

export interface UploadPeppolXmlFileFormValues {
  peppolInvoiceUblFile: ReportFileDTO
}

export interface UploadPeppolXmlFileVariables extends UploadPeppolXmlFileFormValues {
  invoiceId: string
}

export enum UploadPeppolXmlFieldNames {
  FILE = 'peppolInvoiceUblFile',
}

export interface PeppolBulkSendInvoiceResultDTO {
  error: PeppolErrorMessage
  invoiceId: string
  success: boolean
}

export interface PeppolBulkSendResultDTO {
  failedCount: number
  results: Array<PeppolBulkSendInvoiceResultDTO>
  successCount: number
  totalInvoices: number
}

export interface SendToPeppolXmlData {
  sendPeppolDocuments: PeppolBulkSendResultDTO
}

export interface SendToPeppolXmlVariables {
  invoiceIds: string[]
}

export interface DownloadConstructedPeppolUblInvoiceFileData {
  downloadConstructedPeppolUblInvoiceFile: DownloadableFileDTO
}

export interface DownloadConstructedPeppolUblInvoiceFileVariables {
  invoiceId: string
}
